import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { NavBar } from "@/components/NavBar";
import { SiteFooter } from "@/components/SiteFooter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { FileText, ScrollText } from "lucide-react";

const BlockRenderer = ({ block }) => {
  if (block.type === "h2") {
    return <h2 className="font-display text-lg font-semibold mt-6 mb-1.5">{block.text}</h2>;
  }
  if (block.type === "li") {
    return (
      <p className="text-sm text-muted-foreground leading-relaxed pl-4 relative before:content-['\2022'] before:absolute before:left-0 before:text-primary">
        {block.text}
      </p>
    );
  }
  return <p className="text-sm text-muted-foreground leading-relaxed mb-2">{block.text}</p>;
};

export default function LegalPage() {
  const { slug } = useParams();
  const [documents, setDocuments] = useState(null);
  const [current, setCurrent] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get("/legal/documents")
      .then(({ data }) => setDocuments(data.documents || []))
      .catch(() => setDocuments([]));
  }, []);

  useEffect(() => {
    setNotFound(false);
    setCurrent(null);
    if (!slug) return;
    api
      .get(`/legal/documents/${slug}`)
      .then(({ data }) => setCurrent(data.document))
      .catch(() => setNotFound(true));
    window.scrollTo({ top: 0 });
  }, [slug]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <NavBar />
      <main className="flex-1 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
        {!slug ? (
          <div className="space-y-8">
            <div className="space-y-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/15 border border-primary/30">
                <ScrollText className="h-5 w-5 text-primary" />
              </span>
              <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight" data-testid="legal-index-title">
                Policies, Agreements &amp; Disclosures
              </h1>
              <p className="text-muted-foreground max-w-2xl">
                The complete set of GetYourQSIP public policies. Every member agrees to these documents by
                electronic signature when creating an account.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="legal-index-grid">
              {documents === null
                ? Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-28 w-full" />)
                : documents.map((doc) => (
                    <Link key={doc.slug} to={`/legal/${doc.slug}`} data-testid={`legal-card-${doc.slug}`}>
                      <Card className="bg-card border-border h-full transition-colors hover:border-primary/50">
                        <CardHeader className="pb-2">
                          <FileText className="h-4 w-4 text-primary mb-1.5" />
                          <CardTitle className="font-display text-base leading-snug">{doc.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-xs text-muted-foreground">{doc.effective}</p>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
            </div>
          </div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-[260px_1fr]">
            <aside className="hidden lg:block">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">All documents</p>
              <ScrollArea className="h-[70vh] pr-3">
                <nav className="space-y-1" data-testid="legal-sidebar-nav">
                  {(documents || []).map((doc) => (
                    <Link
                      key={doc.slug}
                      to={`/legal/${doc.slug}`}
                      data-testid={`legal-nav-${doc.slug}`}
                      className={`block rounded-md px-3 py-2 text-xs leading-snug transition-colors ${
                        doc.slug === slug
                          ? "bg-primary/10 text-primary font-medium border border-primary/25"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                      }`}
                    >
                      {doc.title}
                    </Link>
                  ))}
                </nav>
              </ScrollArea>
            </aside>
            <article className="min-w-0">
              {notFound ? (
                <div className="rounded-xl border border-border bg-card p-8 text-center space-y-2" data-testid="legal-not-found">
                  <p className="font-display font-semibold">Document not found</p>
                  <Link to="/legal" className="text-sm text-primary hover:underline">
                    Browse all policies
                  </Link>
                </div>
              ) : !current ? (
                <div className="space-y-3">
                  <Skeleton className="h-9 w-2/3" />
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-64 w-full" />
                </div>
              ) : (
                <>
                  <Link to="/legal" className="text-xs text-primary hover:underline" data-testid="legal-back-link">
                    ← All policies
                  </Link>
                  <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight mt-2" data-testid="legal-doc-title">
                    {current.title}
                  </h1>
                  <p className="text-xs text-muted-foreground font-mono mt-1.5 mb-6">{current.effective}</p>
                  <div className="rounded-xl border border-border bg-card p-6 md:p-8" data-testid="legal-doc-body">
                    {current.blocks.map((block, index) => (
                      <BlockRenderer key={index} block={block} />
                    ))}
                  </div>
                </>
              )}
            </article>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
